if (!customElements.get('container-selector')) {
  customElements.define('container-selector', class extends HTMLElement {
    connectedCallback() {
      if (this.onClick) return;
      this.product = JSON.parse(this.querySelector('[data-container-product]').textContent);
      this.steps = this.closest('.shopify-section').querySelector('.container-steps');
      // Account for wrapped steps on mobile in sticky summary and anchor offsets.
      this.stepsResizeObserver = new ResizeObserver(() => this.updateStepsHeight());
      this.stepsResizeObserver.observe(this.steps);
      this.updateStepsHeight();
      this.wasteChosen = false;
      this.selectedSize = null;
      this.selectedFrequency = null;
      this.activeStep = 1;
      this.onClick = this.handleClick.bind(this);
      this.onChange = () => { this.sync(); this.saveState(); };
      this.onStepClick = (event) => {
        const link = event.target.closest('[data-container-step]');
        if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.goToStep(Math.min(Number(link.dataset.containerStep), this.nextStep));
      };
      this.onSubmit = (event) => {
        if (this.nextStep === this.summaryStep && this.currentVariant?.orderable && !this.hasAttribute('aria-busy')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.goToStep(this.nextStep);
      };
      this.onPopState = () => {
        const url = new URL(window.location.href);
        if (!this.hasProductPath(url.pathname)) return;
        const state = window.history.state?.containerSelection;
        const sameProduct = url.pathname.replace(/\/$/, '') === this.dataset.productUrl.replace(/\/$/, '');
        if (sameProduct) {
          this.restoreState(state);
        } else {
          this.loadProduct(url, { history: false, state });
        }
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit, true);
      this.steps.addEventListener('click', this.onStepClick);
      window.addEventListener('popstate', this.onPopState);
      const button = this.querySelector('[id^="ProductSubmitButton-"]');
      if (button) {
        // Native variant requests can finish after the user has changed waste type.
        // Keep both order buttons gated until the explicit selection is complete.
        this.addButtonObserver = new MutationObserver(() => this.guardOrder());
        this.addButtonObserver.observe(button, { subtree: true, childList: true, characterData: true,
          attributes: true, attributeFilter: ['disabled'] });
      }
      this.restoreState(window.history.state?.containerSelection);
    }

    disconnectedCallback() {
      this.request?.abort();
      cancelAnimationFrame(this.scrollFrame);
      this.addButtonObserver?.disconnect();
      this.stepsResizeObserver?.disconnect();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('submit', this.onSubmit, true);
      this.steps?.removeEventListener('click', this.onStepClick);
      window.removeEventListener('popstate', this.onPopState);
      this.onClick = null;
    }

    get nextStep() {
      return !this.wasteChosen ? 1 : !this.selectedSize ? 2 : this.isRolcontainer && !this.selectedFrequency ? 3 : this.summaryStep;
    }

    get isRolcontainer() {
      return this.dataset.containerKind === 'rolcontainer';
    }

    get summaryStep() {
      return this.isRolcontainer ? 4 : 3;
    }

    get selectionHint() {
      const labels = this.product.labels;
      return this.hasAttribute('aria-busy') || this.nextStep === 1 ? labels.chooseWasteHint :
        this.nextStep === 2 ? labels.chooseSizeHint : labels.chooseFrequency;
    }

    get currentVariant() {
      const values = this.product.optionNames.map((name) => this.getRadio(name)?.value);
      return this.product.variants.find((variant) => variant.options.every((value, index) => value === values[index]));
    }

    getContractVariant(duration, reference = this.currentVariant) {
      const durationIndex = this.product.optionNames.indexOf('Duration');
      if (!reference || durationIndex < 0) return;
      return this.product.variants.find((item) => item.options[durationIndex] === duration &&
        item.options.every((value, index) => index === durationIndex || value === reference.options[index])
      );
    }

    contractLabel(variant, baseline) {
      const label = variant?.durationLabel || '';
      if (!variant?.orderable || !baseline?.orderable) return label;
      // Use Shopify's raw prices in cents so VAT display rounding cannot change
      // the percentage. The euro saving below uses the displayed VAT amounts.
      const baseCents = Math.round(baseline.price * 100);
      const priceCents = Math.round(variant.price * 100);
      if (!Number.isFinite(baseCents) || !Number.isFinite(priceCents) || baseCents <= 0 || priceCents < 0 || priceCents >= baseCents) return label;
      const rate = (baseCents - priceCents) / baseCents;
      if (rate < 0.00005) return label;
      const percentage = new Intl.NumberFormat(document.documentElement.lang || 'nl', {
        style: 'percent', maximumFractionDigits: 2,
      }).format(rate);
      return `${label} — ${this.product.labels.discountPercentage.replace('[percentage]', percentage)}`;
    }

    getRadio(name, value) {
      return Array.from(this.querySelectorAll('variant-radios input[type="radio"]')).find((radio) =>
        radio.name === name && (value === undefined ? radio.checked : radio.value === value)
      );
    }

    saveState() {
      window.history.replaceState({ ...window.history.state, containerSelection: {
        path: this.dataset.productUrl, wasteChosen: this.wasteChosen,
        size: this.selectedSize, frequency: this.selectedFrequency,
        duration: this.getRadio('Duration')?.value, step: this.activeStep,
      } }, '', window.location.href);
    }

    stateFromUrl() {
      const variantId = new URL(window.location.href).searchParams.get('variant');
      const variant = this.product.variants.find((item) => String(item.id) === variantId);
      if (!variant) return;
      const option = (name) => variant.options[this.product.optionNames.indexOf(name)];
      return {
        path: this.dataset.productUrl, wasteChosen: true,
        size: option('Size'), frequency: option('Frequency'), duration: option('Duration'),
        step: this.summaryStep,
      };
    }

    restoreState(state) {
      // Cart item links already contain the exact variant. Restore its choices
      // when there is no saved configurator state for this history entry.
      if (state?.path !== this.dataset.productUrl) state = this.stateFromUrl();
      const matches = state?.path === this.dataset.productUrl;
      this.wasteChosen = matches && state.wasteChosen === true;
      this.selectedSize = null;
      this.selectedFrequency = null;
      if (matches && state.duration) this.getRadio('Duration', state.duration)?.click();
      if (this.wasteChosen && state.size) this.selectSize(state.size);
      if (this.selectedSize && state.frequency) this.selectFrequency(state.frequency);
      this.activeStep = matches ? Math.min(state.step || this.nextStep, this.nextStep) : 1;
      this.sync();
      this.revealActiveStep();
      if (matches) this.saveState();
    }

    updateStepsHeight() {
      this.style.setProperty('--container-steps-height', `${Math.ceil(this.steps.getBoundingClientRect().height)}px`);
      this.revealActiveStep();
    }

    revealActiveStep() {
      const link = this.steps.querySelector('[aria-current="step"]');
      const track = link?.parentElement;
      if (!track || track.scrollWidth <= track.clientWidth) return;
      const bounds = track.getBoundingClientRect();
      const active = link.getBoundingClientRect();
      const inset = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
      const left = active.left < bounds.left + inset ? active.left - bounds.left - inset :
        active.right > bounds.right - inset ? active.right - bounds.right + inset : 0;
      // Scroll just the steps, without moving the product page vertically.
      if (left) track.scrollBy({ left,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    goToStep(step) {
      this.activeStep = step;
      this.sync();
      this.saveState();
      cancelAnimationFrame(this.scrollFrame);
      // Measure after the replacement sections and sticky navigation have laid out.
      this.scrollFrame = requestAnimationFrame(() => {
        if (!this.isConnected || this.hasAttribute('aria-busy')) return;
        const link = this.steps.querySelector(`[data-container-step="${step}"]`);
        const target = link && document.getElementById(link.hash.slice(1));
        if (!target) return;
        this.updateStepsHeight();
        const heading = target.querySelector('h3');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }
        const offset = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
        window.scrollTo({ top: window.scrollY + target.getBoundingClientRect().top - offset,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      });
    }

    hasProductPath(path) {
      return Array.from(this.querySelectorAll('.product__link')).some((link) =>
        new URL(link.dataset.productUrl, window.location.href).pathname.replace(/\/$/, '') === path.replace(/\/$/, '')
      );
    }

    handleClick(event) {
      const size = event.target.closest('.product__size--value');
      if (size) {
        if (this.hasAttribute('aria-busy') || !this.wasteChosen || size.disabled) return;
        if (this.selectSize(size.value)) this.goToStep(this.nextStep);
        return;
      }
      const frequency = event.target.closest('.product__frequency--value');
      if (frequency) {
        if (this.hasAttribute('aria-busy') || !this.selectedSize || frequency.disabled) return;
        if (this.selectFrequency(frequency.value)) this.goToStep(this.summaryStep);
        return;
      }
      // Raised content can cover the card's overlay. Keep secondary links independent.
      const interactive = event.target.closest('a, button, input, select, textarea, label');
      const link = event.target.closest('.product__link, .product__select') ||
        (!interactive && event.target.closest('.product__type')?.querySelector('.product__link'));
      if (!link || link.disabled || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (this.hasAttribute('aria-busy')) return;
      const url = new URL(link.dataset.productUrl, window.location.href);
      if (url.origin !== window.location.origin || !this.hasProductPath(url.pathname)) return;
      event.preventDefault();
      if (url.pathname.replace(/\/$/, '') === this.dataset.productUrl.replace(/\/$/, '')) {
        this.request?.abort();
        this.setLoading(false);
        this.wasteChosen = true;
        this.goToStep(2);
        return;
      }
      url.hash = '';
      url.pathname = url.pathname.replace(/\/$/, '');
      // A different waste type starts its own size choice; never assume a format.
      this.loadProduct(url, { history: true, focus: true });
    }

    selectSize(value) {
      if (!this.wasteChosen) return false;
      const radio = this.getRadio('Size', value);
      if (!radio || radio.disabled) return false;
      if (value !== this.selectedSize) this.selectedFrequency = null;
      this.selectedSize = value;
      if (!radio.checked) radio.click();
      this.activeStep = this.nextStep;
      this.sync();
      this.saveState();
      return true;
    }

    selectFrequency(value) {
      const radio = this.getRadio('Frequency', value);
      if (!this.selectedSize || !radio || radio.disabled || radio.classList.contains('disabled')) return false;
      this.selectedFrequency = value;
      if (!radio.checked) radio.click();
      this.sync();
      this.saveState();
      return true;
    }

    guardOrder() {
      const button = this.querySelector('[id^="ProductSubmitButton-"]');
      if (!button) return;
      const label = button.querySelector('span');
      const text = window.variantStrings.addToCart;
      if (label && label.textContent !== text) label.textContent = text;
      if (this.nextStep === this.summaryStep && this.currentVariant?.orderable && !this.hasAttribute('aria-busy')) return;
      if (!button.disabled) button.disabled = true;
    }

    sync() {
      this.querySelectorAll('.product__type').forEach((card) => {
        const choice = card.querySelector('.product__link');
        const selected = this.wasteChosen && new URL(choice.dataset.productUrl, window.location.href).pathname.replace(/\/$/, '') === this.dataset.productUrl;
        card.classList.toggle('active', selected);
        choice.setAttribute('aria-pressed', String(selected));
        choice.disabled = this.hasAttribute('aria-busy');
        const action = card.querySelector('.product__select');
        action.setAttribute('aria-pressed', String(selected));
        action.disabled = this.hasAttribute('aria-busy');
        action.classList.toggle('button--secondary', !selected);
        action.classList.toggle('button--secondary-selected', selected);
        action.textContent = selected ? this.product.labels.selected : this.product.labels.select;
      });
      this.querySelectorAll('.product__size--value').forEach((button) => {
        const selected = button.value === this.selectedSize;
        const radio = Array.from(this.querySelectorAll('input[name="Size"]')).find((input) => input.value === button.value);
        button.disabled = !this.wasteChosen || !!radio?.disabled || this.hasAttribute('aria-busy');
        button.classList.toggle('active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      const hint = this.querySelector('[data-size-hint]');
      if (hint) hint.hidden = this.wasteChosen;
      if (this.isRolcontainer) {
        this.querySelectorAll('.product__frequency--value').forEach((button) => {
          const selected = button.value === this.selectedFrequency;
          const radio = this.getRadio('Frequency', button.value);
          button.disabled = !this.selectedSize || !radio || radio.disabled || radio.classList.contains('disabled') || this.hasAttribute('aria-busy');
          button.classList.toggle('active', selected);
          button.setAttribute('aria-pressed', String(selected));
        });
        const frequencyHint = this.querySelector('[data-frequency-hint]');
        if (frequencyHint) {
          frequencyHint.hidden = !!this.selectedSize;
          frequencyHint.textContent = this.wasteChosen ? this.product.labels.chooseSizeHint : this.product.labels.chooseWasteHint;
        }
        this.querySelector('.product__selected-variant--frequency').textContent = this.selectedFrequency || this.product.labels.chooseFrequency;
        this.querySelectorAll('input[name="Duration"]').forEach((radio) => {
          this.querySelector(`label[for="${radio.id}"] .radio-circle`)?.classList.toggle('checked', radio.checked);
        });
      }
      this.querySelector('.product__selected-variant--title').textContent = this.wasteChosen ? this.product.title : this.product.labels.chooseWaste;
      this.querySelector('.product__selected-variant--size').textContent = this.selectedSize || this.product.labels.chooseSize;
      const variant = this.currentVariant;
      const complete = this.nextStep === this.summaryStep;
      const price = this.querySelector('.total-price');
      if (price) price.textContent = complete && variant ? variant.inclusiveMoney : '—';
      const exclusivePrice = this.querySelector('[data-price-exclusive]');
      if (exclusivePrice) exclusivePrice.textContent = complete && variant ? variant.exclusiveMoney : '—';
      if (this.isRolcontainer) {
        // Compare the same size/frequency against its actual flexible tariff.
        // A percentage in a catalog label is not a reliable reference price.
        const baseline = this.getContractVariant('Maandelijks opzegbaar', variant);
        this.querySelectorAll('input[name="Duration"]').forEach((radio) => {
          const contract = this.getContractVariant(radio.value, variant);
          const fallback = this.product.variants.find((item) => item.options[this.product.optionNames.indexOf('Duration')] === radio.value)?.durationLabel || radio.value;
          const label = this.contractLabel(contract, complete ? baseline : null) || fallback;
          const text = this.querySelector(`label[for="${radio.id}"] [data-duration-label]`);
          if (text && text.textContent !== label) text.textContent = label;
          const selectedDuration = this.querySelector('.product__selected-variant--duration');
          if (radio.checked && selectedDuration.textContent !== label) selectedDuration.textContent = label;
        });
        const comparable = complete && variant && baseline?.orderable && baseline.inclusivePrice >= variant.inclusivePrice;
        const overview = this.querySelector('.product__cart-calculation--overview');
        overview.hidden = complete && !comparable;
        this.querySelector('.subtotal-price').textContent = comparable ? baseline.inclusiveMoney : '—';
        const saving = comparable ? Math.round((baseline.inclusivePrice - variant.inclusivePrice) * 100) / 100 : 0;
        const money = new Intl.NumberFormat(document.documentElement.lang || 'nl', { style: 'currency', currency: this.product.currency });
        this.querySelector('.discount-price').textContent = comparable ? money.format(saving ? -saving : 0) : '—';
      }
      const button = this.querySelector('[id^="ProductSubmitButton-"]');
      if (button && complete && !this.hasAttribute('aria-busy') && !button.classList.contains('loading')) {
        button.disabled = !variant?.orderable;
        const label = button.querySelector('span');
        const text = window.variantStrings.addToCart;
        if (label && label.textContent !== text) label.textContent = text;
      }
      if (complete && variant?.orderable && !this.hasAttribute('aria-busy') && this.product.trackViews && window.dataLayer && this.lastTrackedVariant !== variant.id) {
        this.lastTrackedVariant = variant.id;
        window.dataLayer.push({ event: 'container_configuration_ready', container: {
          product: this.product.title, variant_id: String(variant.id),
          size: this.selectedSize, frequency: this.selectedFrequency,
          duration: variant.durationLabel || '', value: variant.price, currency: this.product.currency,
        } });
      }
      this.guardOrder();
      this.steps.querySelectorAll('[data-container-step]').forEach((link) => {
        const step = Number(link.dataset.containerStep);
        link.classList.toggle('is-complete', step < this.nextStep);
        link.setAttribute('aria-disabled', String(step > this.nextStep));
        if (step === this.activeStep) link.setAttribute('aria-current', 'step');
        else link.removeAttribute('aria-current');
      });
    }

    setLoading(loading, url) {
      if (loading) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
      this.querySelectorAll('.product__type').forEach((card) => {
        const link = card.querySelector('.product__link');
        card.classList.toggle('container-choice-loading', loading && new URL(link.dataset.productUrl, window.location.href).pathname.replace(/\/$/, '') === url?.pathname);
      });
      this.sync();
    }

    async loadProduct(url, options) {
      const hero = document.querySelector('.container-hero')?.closest('.shopify-section');
      const main = this.closest('.shopify-section');
      const rules = document.querySelector('[data-container-waste-rules]')?.closest('.shopify-section');
      const faq = document.querySelector('[data-container-faq]')?.closest('.shopify-section');
      if (!hero || !main) return;
      this.request?.abort();
      cancelAnimationFrame(this.scrollFrame);
      const request = this.request = new AbortController();
      const heroId = hero.id.replace('shopify-section-', '');
      const mainId = main.id.replace('shopify-section-', '');
      const rulesId = rules?.id.replace('shopify-section-', '');
      const faqId = faq?.id.replace('shopify-section-', '');
      const requestUrl = new URL(url);
      requestUrl.searchParams.set('sections', [heroId, mainId, rulesId, faqId].filter(Boolean).join(','));
      this.setLoading(true, url);
      this.querySelector('product-form')?.handleErrorMessage();

      try {
        const response = await fetch(requestUrl, { signal: request.signal });
        if (!response.ok) throw new Error('Container sections unavailable');
        const sections = await response.json();
        const parser = new DOMParser();
        const nextHero = parser.parseFromString(sections[heroId] || '', 'text/html').querySelector('.shopify-section');
        const nextMain = parser.parseFromString(sections[mainId] || '', 'text/html').querySelector('.shopify-section');
        const nextRules = rulesId
          ? parser.parseFromString(sections[rulesId] || '', 'text/html').querySelector('.shopify-section')
          : null;
        const nextFaq = faqId
          ? parser.parseFromString(sections[faqId] || '', 'text/html').querySelector('.shopify-section')
          : null;
        if (!nextHero?.querySelector('.container-hero') || !nextMain?.querySelector('container-selector [data-container-product]') ||
          (rules && !nextRules?.querySelector('[data-container-waste-rules]')) ||
          (faq && !nextFaq?.querySelector('[data-container-faq]'))) {
          throw new Error('Incomplete container sections');
        }
        if (request.signal.aborted || !this.isConnected) return;

        const anchorTop = this.querySelector('.product__type--wrapper').getBoundingClientRect().top;
        // Import markup only. Existing custom elements initialize the new forms;
        // executable scripts from the section response are never evaluated.
        hero.replaceWith(document.importNode(nextHero, true));
        const importedMain = document.importNode(nextMain, true);
        main.replaceWith(importedMain);
        if (rules) rules.replaceWith(document.importNode(nextRules, true));
        if (faq) faq.replaceWith(document.importNode(nextFaq, true));
        const selector = importedMain.querySelector('container-selector');
        if (options.history) window.history.pushState({}, '', url);
        if (options.history) {
          selector.wasteChosen = true;
          selector.selectedSize = null;
          selector.selectedFrequency = null;
          selector.activeStep = 2;
          selector.sync();
          selector.saveState();
        } else selector.restoreState(options.state);
        selector.updatePageDetails();
        selector.trackProductView();
        if (options.focus) selector.goToStep(2);
        else {
          const nextTop = selector.querySelector('.product__type--wrapper').getBoundingClientRect().top;
          window.scrollBy({ top: nextTop - anchorTop, behavior: 'instant' });
        }
      } catch (error) {
        if (error.name === 'AbortError' || !this.isConnected) return;
        this.setLoading(false);
        this.querySelector('product-form')?.handleErrorMessage(window.cartStrings.error);
      }
    }

    updatePageDetails() {
      document.title = this.product.pageTitle;
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.href = this.product.url;
      const description = document.querySelector('meta[name="description"]');
      if (description) description.content = this.product.description;
      const socialValues = {
        'og:title': this.product.socialTitle,
        'twitter:title': this.product.socialTitle,
        'og:description': this.product.description,
        'twitter:description': this.product.description,
        'og:url': this.product.url,
        'og:price:amount': String(this.product.socialPrice),
      };
      for (const [name, content] of Object.entries(socialValues)) {
        const meta = document.querySelector(`meta[property="${name}"], meta[name="${name}"]`);
        if (meta) meta.content = content;
      }
    }

    trackProductView() {
      if (!this.product.trackViews || !window.dataLayer) return;
      const id = this.querySelector('.product-variant-id')?.value;
      const variant = this.product.variants.find((item) => String(item.id) === id);
      if (!variant) return;
      window.dataLayer.push({
        event: 'ecomProductDetailPage',
        ecommerce: {
          currencyCode: this.product.currency,
          detail: {
            actionField: { list: 'product detail page' },
            products: [{ name: this.product.title, id: variant.sku ? variant.sku.split('-')[0] : String(variant.id), price: String(variant.price), brand: this.product.brand, category: this.product.category, variant: variant.sku }],
          },
        },
      });
    }
  });
}
